/**
 * Lois Speck Shop - Unified Modifications
 * Handles all custom CSS and JS modifications for the shop
 */

(function() {
  'use strict';

  // ========== BANNER CONFIGURATION ==========
  // Available themes: 'christmas', 'easter', 'fruehjahrsputz', 'maigenuss', 'newyear', 'olympia', 'discount', 'brandred', 'none'
  const BANNER_THEME = 'brandred';
  
  // Discount percentage (number only, e.g., 10 for -10%)
  const BANNER_DISCOUNT_PERCENT = 10;

  // Olympia icon: same Assets folder as this script. We derive the asset base URL
  // from this script's src so the image works without setting anything in theme.liquid.
  // Override with window.LOIS_OLYMPIA_ICON_URL in theme.liquid if needed.
  const OLYMPIA_ICON_FILENAME = 'lois-olympia-2026-ai.jpg';
  const OLYMPIA_ICON_URL = (function() {
    if (typeof window !== 'undefined' && window.LOIS_OLYMPIA_ICON_URL) {
      return window.LOIS_OLYMPIA_ICON_URL;
    }
    const script = document.querySelector('script[src*="lois-mods.js"]');
    if (script && script.src) {
      const base = script.src.replace(/\/[^/]*$/, '/');
      return base + OLYMPIA_ICON_FILENAME;
    }
    return '/' + OLYMPIA_ICON_FILENAME;
  })();
  // ==========================================

  // Configuration
  const config = {
    selectors: {
      // Using ID selector as it's more specific for the Essential Announcement Bar app
      announcementTitle: '#essential_annoucement_bar_title_47ae3aa3-da51-4f00-90b0-c5c3659442b9',
      // Fallback to class-based selector if ID changes
      announcementTitleClass: '[class*="essential_annoucement_bar_title_container_"]',
      // AppHero banner selector (currently in use)
      appHeroBanner: '.aph_bar_message_body',
      shopNowButton: '[class*="essential_annoucement_bar_cta_"]'
    }
  };

  // Banner theme definitions with {percent} placeholder
  const bannerThemes = {
    christmas: {
      de: {
        part1: "✨🎅 Festliche Weihnachtstage:",
        part2: "-{percent}% auf alle Spezialitäten!!!"
      },
      it: {
        part1: "✨🎅 Giorni festivi di Natale:",
        part2: "-{percent}% su tutte le specialità!!!"
      }
    },
    easter: {
      de: {
        part1: "🐣🌷 Frohe Ostern:",
        part2: "-{percent}% auf alle Spezialitäten!!!"
      },
      it: {
        part1: "🐣🌷 Buona Pasqua:",
        part2: "-{percent}% su tutte le specialità!!!"
      }
    },
    fruehjahrsputz: {
      de: {
        part1: "🧹 Frühjahrsputz:",
        part2: "-{percent}% auf alle Spezialitäten!!!"
      },
      it: {
        part1: "🧹 Pulizie di primavera:",
        part2: "-{percent}% su tutte le specialità!!!"
      }
    },
    maigenuss: {
      de: {
        part1: "🌷 Mai ist Genusszeit –",
        part2: "{percent} % Rabatt im ganzen Shop"
      },
      it: {
        part1: "🌷 Maggio è tempo di gusto –",
        part2: "{percent}% di sconto in tutto il negozio"
      }
    },
    newyear: {
      de: {
        part1: "🎉🥂 Willkommen {year}:",
        part2: "-{percent}% auf alle Spezialitäten!!!"
      },
      it: {
        part1: "🎉🥂 Benvenuto {year}:",
        part2: "-{percent}% su tutte le specialità!!!"
      }
    },
    olympia: {
      de: {
        part1: "<img src=\"{olympiaIconUrl}\" alt=\"Olympia\" class=\"lois-banner-icon\" width=\"18\" height=\"18\"> Olympia 2026:",
        part2: "-{percent}% auf alle Spezialitäten!!!"
      },
      it: {
        part1: "<img src=\"{olympiaIconUrl}\" alt=\"Olympia\" class=\"lois-banner-icon\" width=\"18\" height=\"18\"> Olimpiadi 2026:",
        part2: "-{percent}% su tutte le specialità!!!"
      }
    },
    discount: {
      de: {
        part1: "",
        part2: "-{percent}% auf alle Spezialitäten!!!"
      },
      it: {
        part1: "",
        part2: "-{percent}% su tutte le specialità!!!"
      }
    },
    brandred: {
      de: {
        part1: "",
        part2: "-{percent}% auf alle Spezialitäten"
      },
      it: {
        part1: "",
        part2: "-{percent}% su tutte le specialità"
      }
    },
    none: null  // Disables banner entirely
  };

  const translations = {
    de: {
      shopNow: "Jetzt kaufen!"
    },
    it: {
      shopNow: "Acquista ora!"
    }
  };

  // Detect page language
  function getPageLanguage() {
    // Check URL parameter first (more reliable for locale detection)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocale = urlParams.get('locale');
    if (urlLocale && urlLocale.toLowerCase().startsWith('it')) {
      return 'it';
    }
    
    // Check current URL path for locale indicators
    const currentUrl = window.location.href;
    if (currentUrl.includes('locale=it') || currentUrl.includes('/it/')) {
      return 'it';
    }
    
    // Fallback to HTML lang attribute
    const lang = document.documentElement.lang || 'de';
    // Normalize language code (handle both 'it' and 'it-IT')
    const detectedLang = lang.toLowerCase().startsWith('it') ? 'it' : 'de';
    return detectedLang;
  }

  // Create custom red banner at the top of the page
  function createCustomBanner() {
    // Check if theme is set to 'none' - skip banner creation
    if (BANNER_THEME === 'none' || !bannerThemes[BANNER_THEME]) {
      return;
    }

    // Skip on the storefront cart page (matches /cart, /de/cart, /it/cart, with optional trailing slash)
    if (/^\/(de\/|it\/)?cart\/?$/.test(window.location.pathname)) {
      return;
    }

    // Check if banner already exists
    if (document.getElementById('lois-custom-banner')) {
      return;
    }

    const lang = getPageLanguage();
    const themeTexts = bannerThemes[BANNER_THEME][lang] || bannerThemes[BANNER_THEME].de;
    
    // Get current year for newyear theme
    const currentYear = new Date().getFullYear();
    
    // Replace {percent} placeholder with configured discount value and {year} with current year
    const bannerText = {
      part1: themeTexts.part1.replace('{percent}', BANNER_DISCOUNT_PERCENT).replace('{year}', currentYear).replace('{olympiaIconUrl}', OLYMPIA_ICON_URL),
      part2: themeTexts.part2.replace('{percent}', BANNER_DISCOUNT_PERCENT).replace('{year}', currentYear).replace('{olympiaIconUrl}', OLYMPIA_ICON_URL)
    };
    
    // Use the exact red color from "Jetzt probieren" button
    const redColor = '#8b0000';

    // Create banner element
    const banner = document.createElement('div');
    banner.id = 'lois-custom-banner';
    banner.setAttribute('role', 'banner');
    banner.setAttribute('aria-label', 'Announcement');
    banner.setAttribute('data-banner-theme', BANNER_THEME);
    
    // Theme-specific background and text color
    const isEaster = BANNER_THEME === 'easter';
    const isFruehjahrsputz = BANNER_THEME === 'fruehjahrsputz';
    const isMaigenuss = BANNER_THEME === 'maigenuss';
    const isBrandred = BANNER_THEME === 'brandred';
    var bgStyle, textColor;
    if (isEaster) {
      bgStyle = 'background: linear-gradient(135deg, #e8f5e2 0%, #d4ecc4 30%, #e0f0d8 60%, #d8ecd0 100%) !important;';
      textColor = '#2a5a18';
    } else if (isFruehjahrsputz) {
      // "Stube Linen" palette — linen cream gradient that sits under the dark brand header
      // without clashing. Brass-amber rays + brass sparks replace the previous bright yellow.
      bgStyle = 'background: linear-gradient(135deg, #f0e6d2 0%, #e8dcc8 35%, #ede0c8 65%, #f0e6d2 100%) !important;';
      textColor = '#3a2a1a';
    } else if (isMaigenuss) {
      // "Stube Linen" palette with sage shift in the middle — drifting moss leaves on linen.
      bgStyle = 'background: linear-gradient(135deg, #f0e6d2 0%, #dde0c8 40%, #e8dcc8 70%, #f0e6d2 100%) !important;';
      textColor = '#3a2a1a';
    } else if (isBrandred) {
      // Brand Red — solid dark burgundy matching the shop's CTA button color,
      // with cream text and subtle top/bottom border accents.
      bgStyle = 'background-color: #7d1219 !important;';
      textColor = '#f4f2ec';
    } else {
      bgStyle = 'background-color: ' + redColor + ' !important;';
      textColor = '#ffffff';
    }

    // Set inline styles for immediate rendering with !important to override any theme styles
    // Banner sits at the top of the document flow (not fixed) — scrolls away with the page,
    // so it never overlaps content like the cart drawer or sticky headers.
    banner.style.cssText = `
      position: relative !important;
      width: 100% !important;
      ${bgStyle}
      color: ${textColor} !important;
      text-align: center !important;
      padding: ${isBrandred ? '13px 24px' : '15px 24px'} !important;
      font-size: ${isBrandred ? '14px' : '14px'} !important;
      font-weight: ${(isEaster || isFruehjahrsputz || isMaigenuss) ? '600' : '500'} !important;
      letter-spacing: ${isBrandred ? '0.5px' : '0'} !important;
      line-height: 1.5 !important;
      z-index: 1 !important;
      box-sizing: border-box !important;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
      ${isBrandred ? 'border-top: 1px solid rgba(244,242,236,0.25) !important; border-bottom: 1px solid rgba(244,242,236,0.25) !important;' : ''}
      display: block !important;
      visibility: visible !important;
      margin: 0 !important;
      border: none !important;
      overflow: hidden !important;
    `;

    // Create banner content with HTML structure for styling
    // Handle empty part1 (for 'discount' theme) - don't add extra space
    const part1HTML = bannerText.part1 ? `<span class="banner-part1">${bannerText.part1}</span> ` : '';

    // Generate decoration HTML for themed banners — use inline styles for position/timing
    // because nth-child selectors break when mixed with other child elements
    var confettiHTML = '';
    if (isMaigenuss) {
      // 6 cut tulips drift across at varied heights, with three different flight paths
      // (A: gentle sway, B: counter-sway, C: full tumble) for natural variety.
      var tulipColors = ['red', 'gold', 'pink', 'cream', 'red', 'gold'];
      var tulipPositions = [
        { top: '4px',  fly: 'A', dur: '14s', delay: '0s'   },
        { top: '28px', fly: 'B', dur: '17s', delay: '-3s'  },
        { top: '12px', fly: 'C', dur: '19s', delay: '-7s'  },
        { top: '24px', fly: 'A', dur: '16s', delay: '-11s' },
        { top: '6px',  fly: 'B', dur: '13s', delay: '-2s'  },
        { top: '18px', fly: 'C', dur: '21s', delay: '-14s' }
      ];
      for (var i = 0; i < tulipPositions.length; i++) {
        var tp = tulipPositions[i];
        var color = tulipColors[i];
        // &nbsp; in each child defeats Shopify base.css `div:empty { display:none }`,
        // matching the same workaround used by .lois-leaf / .lois-sun-spark / .lois-confetti.
        confettiHTML +=
          '<div class="lois-tulip lois-tulip-' + color + '" style="top:' + tp.top + ';animation:loisTulipFly' + tp.fly + ' ' + tp.dur + ' linear ' + tp.delay + ' infinite;">' +
            '<div class="lois-tulip-head">&nbsp;</div>' +
            '<div class="lois-tulip-stem">&nbsp;</div>' +
            '<div class="lois-tulip-leaf">&nbsp;</div>' +
          '</div>';
      }
    } else if (isFruehjahrsputz) {
      var sparkPositions = [
        { left: '5%',  top: '8px',  delay: '0s' },
        { left: '15%', top: '28px', delay: '-1.0s' },
        { left: '25%', top: '10px', delay: '-0.4s' },
        { left: '38%', top: '26px', delay: '-2.2s' },
        { left: '50%', top: '6px',  delay: '-1.5s' },
        { left: '62%', top: '30px', delay: '-0.7s' },
        { left: '72%', top: '8px',  delay: '-1.8s' },
        { left: '82%', top: '24px', delay: '-0.2s' },
        { left: '92%', top: '12px', delay: '-2.6s' },
        { left: '48%', top: '20px', delay: '-1.2s' }
      ];
      for (var i = 0; i < sparkPositions.length; i++) {
        var sp = sparkPositions[i];
        confettiHTML += '<div class="lois-sun-spark" style="left:' + sp.left + ';top:' + sp.top + ';animation-delay:' + sp.delay + ';">&nbsp;</div>';
      }
    } else if (isEaster) {
      var confettiTypes = ['egg', 'dot', 'petal', 'diamond'];
      var confettiColors = ['c-pink', 'c-coral', 'c-mint', 'c-lilac', 'c-peach', 'c-green', 'c-sky', 'c-yellow'];
      var confettiPositions = [
        { left: '2%',  dur: '4.0s', delay: '-0.2s' },
        { left: '7%',  dur: '3.6s', delay: '-1.8s' },
        { left: '13%', dur: '4.8s', delay: '-0.6s' },
        { left: '19%', dur: '3.3s', delay: '-2.5s' },
        { left: '26%', dur: '4.4s', delay: '-1.1s' },
        { left: '33%', dur: '3.7s', delay: '-3.2s' },
        { left: '40%', dur: '4.2s', delay: '-0.4s' },
        { left: '48%', dur: '3.5s', delay: '-2.9s' },
        { left: '55%', dur: '4.6s', delay: '-1.4s' },
        { left: '62%', dur: '3.4s', delay: '-3.6s' },
        { left: '70%', dur: '4.1s', delay: '-0.9s' },
        { left: '77%', dur: '5.0s', delay: '-2.1s' },
        { left: '84%', dur: '3.2s', delay: '-1.7s' },
        { left: '91%', dur: '4.3s', delay: '-0.1s' },
        { left: '96%', dur: '3.8s', delay: '-2.6s' }
      ];
      for (var i = 0; i < 15; i++) {
        var type = confettiTypes[i % confettiTypes.length];
        var color = confettiColors[i % confettiColors.length];
        var pos = confettiPositions[i];
        confettiHTML += '<div class="lois-confetti lois-confetti-' + type + ' lois-' + color + '" style="left:' + pos.left + ';animation:loisConfettiFall ' + pos.dur + ' linear ' + pos.delay + ' infinite !important;">&nbsp;</div>';
      }
    }

    // Inject styles into <head> for reliability (Shopify can strip inline <style> from injected elements)
    var styleEl = document.createElement('style');
    styleEl.id = 'lois-banner-styles';
    styleEl.textContent = `
      #lois-custom-banner {
        white-space: nowrap;
      }
      #lois-custom-banner .banner-part2 {
        font-weight: 700;
      }
      #lois-custom-banner .lois-banner-icon {
        vertical-align: middle;
        display: inline-block;
      }
      #lois-custom-banner[data-banner-theme="olympia"] {
        padding: 18px 24px !important;
        font-size: 17px !important;
      }
      #lois-custom-banner[data-banner-theme="olympia"] .lois-banner-icon {
        width: 26px !important;
        height: 26px !important;
      }
      #lois-custom-banner[data-banner-theme="easter"] .banner-part2 strong {
        color: #2d6b14;
      }
      #lois-custom-banner[data-banner-theme="easter"] .banner-part1,
      #lois-custom-banner[data-banner-theme="easter"] .banner-part2 {
        position: relative;
        z-index: 2;
      }
      #lois-custom-banner[data-banner-theme="fruehjahrsputz"] .banner-part1,
      #lois-custom-banner[data-banner-theme="fruehjahrsputz"] .banner-part2 {
        position: relative;
        z-index: 2;
        text-shadow: 0 1px 2px rgba(245,240,230,0.55);
      }
      #lois-custom-banner[data-banner-theme="fruehjahrsputz"] .banner-part2 strong {
        color: #8b0000;
      }
      #lois-custom-banner[data-banner-theme="fruehjahrsputz"]::before {
        content: '';
        position: absolute;
        top: -50%;
        left: 50%;
        width: 200%;
        height: 200%;
        background: conic-gradient(
          from 0deg at 50% 0%,
          transparent 0deg, rgba(168,122,58,0.08) 15deg, transparent 30deg,
          rgba(168,122,58,0.06) 45deg, transparent 60deg, rgba(168,122,58,0.08) 75deg,
          transparent 90deg, rgba(168,122,58,0.05) 105deg, transparent 120deg,
          rgba(168,122,58,0.07) 135deg, transparent 150deg, rgba(168,122,58,0.06) 165deg,
          transparent 180deg, rgba(168,122,58,0.08) 195deg, transparent 210deg,
          rgba(168,122,58,0.05) 225deg, transparent 240deg, rgba(168,122,58,0.07) 255deg,
          transparent 270deg, rgba(168,122,58,0.06) 285deg, transparent 300deg,
          rgba(168,122,58,0.08) 315deg, transparent 330deg, rgba(168,122,58,0.05) 345deg,
          transparent 360deg
        );
        transform: translateX(-50%);
        animation: loisSunRotate 20s linear infinite;
        z-index: 0;
        pointer-events: none;
      }
      .lois-sun-spark {
        position: absolute !important;
        width: 4px !important;
        height: 4px !important;
        background: #a87a3a !important;
        border-radius: 50% !important;
        z-index: 1 !important;
        font-size: 0 !important;
        line-height: 0 !important;
        box-shadow: 0 0 4px rgba(168,122,58,0.4) !important;
        animation: loisSunSpark 3s ease-in-out infinite !important;
      }
      @keyframes loisSunRotate {
        0%   { transform: translateX(-50%) rotate(0deg); }
        100% { transform: translateX(-50%) rotate(360deg); }
      }
      @keyframes loisSunSpark {
        0%, 100% { opacity: 0; transform: scale(0.3); }
        50%      { opacity: 0.6; transform: scale(1.4); }
      }
      #lois-custom-banner[data-banner-theme="maigenuss"] .banner-part1,
      #lois-custom-banner[data-banner-theme="maigenuss"] .banner-part2 {
        position: relative;
        z-index: 3;
        text-shadow: 0 1px 2px rgba(245,240,230,0.55);
      }
      #lois-custom-banner[data-banner-theme="maigenuss"] .banner-part2 strong {
        color: #8b0000;
      }
      /* Soft warm sun glow at top center — atmosphere, no rotation */
      #lois-custom-banner[data-banner-theme="maigenuss"]::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(ellipse 70% 80% at 50% -10%, rgba(212,168,58,0.22), transparent 65%);
        z-index: 0;
        pointer-events: none;
      }
      /* Cut tulip = wrapper + head (clip-path goblet) + tiny stem stub + small leaf */
      .lois-tulip {
        position: absolute !important;
        width: 12px !important;
        height: 18px !important;
        z-index: 2 !important;
        font-size: 0 !important;
        line-height: 0 !important;
      }
      .lois-tulip-head {
        position: absolute !important;
        top: 0 !important;
        left: 50% !important;
        width: 11px !important;
        height: 13px !important;
        margin-left: -5.5px !important;
        background: var(--tulip) !important;
        clip-path: polygon(50% 0%, 32% 28%, 14% 4%, 0% 55%, 12% 100%, 88% 100%, 100% 55%, 86% 4%, 68% 28%) !important;
        box-shadow: inset 0 -1px 1px rgba(0,0,0,0.15) !important;
      }
      .lois-tulip-stem {
        position: absolute !important;
        top: 11px !important;
        left: 50% !important;
        width: 1.5px !important;
        height: 6px !important;
        margin-left: -0.75px !important;
        background: linear-gradient(180deg, #7ba368, #5c8048) !important;
        border-radius: 0 0 50% 50% !important;
      }
      .lois-tulip-leaf {
        position: absolute !important;
        top: 13px !important;
        left: 56% !important;
        width: 5px !important;
        height: 3px !important;
        background: linear-gradient(180deg, #7ba368, #5c8048) !important;
        border-radius: 0 100% 0 100% !important;
        transform: rotate(35deg) !important;
        transform-origin: 0 0 !important;
      }
      .lois-tulip-red    { --tulip: #8b0000; }
      .lois-tulip-gold   { --tulip: #d4a83a; }
      .lois-tulip-pink   { --tulip: #d4969a; }
      .lois-tulip-cream  { --tulip: #f5e8d0; }
      .lois-tulip-cream .lois-tulip-head {
        box-shadow: inset 0 -1px 1px rgba(120,90,40,0.18), inset 0 0 0 0.5px rgba(120,90,40,0.22) !important;
      }
      @keyframes loisTulipFlyA {
        0%   { left: -8%;  opacity: 0; transform: rotate(-25deg) scale(0.95); }
        8%   { opacity: 0.95; }
        50%  { transform: rotate(15deg) scale(1.05); }
        92%  { opacity: 0.95; }
        100% { left: 108%; opacity: 0; transform: rotate(35deg) scale(0.95); }
      }
      @keyframes loisTulipFlyB {
        0%   { left: -8%;  opacity: 0; transform: rotate(20deg) scale(0.9); }
        8%   { opacity: 0.95; }
        50%  { transform: rotate(-10deg) scale(1); }
        92%  { opacity: 0.95; }
        100% { left: 108%; opacity: 0; transform: rotate(-40deg) scale(0.9); }
      }
      @keyframes loisTulipFlyC {
        0%   { left: -8%;  opacity: 0; transform: rotate(0deg) scale(0.92); }
        8%   { opacity: 0.92; }
        92%  { opacity: 0.92; }
        100% { left: 108%; opacity: 0; transform: rotate(360deg) scale(0.92); }
      }
      .lois-confetti {
        position: absolute !important;
        top: 0 !important;
        display: block !important;
        z-index: 1 !important;
        font-size: 0 !important;
        line-height: 0 !important;
      }
      .lois-confetti-egg {
        width: 9px; height: 12px;
        border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
      }
      .lois-confetti-dot {
        width: 6px; height: 6px;
        border-radius: 50%;
      }
      .lois-confetti-diamond {
        width: 7px; height: 7px;
        border-radius: 2px;
      }
      .lois-confetti-petal {
        width: 8px; height: 13px;
        border-radius: 50% 0 50% 0;
      }
      .lois-c-pink   { background: #f48fb1 !important; }
      .lois-c-coral  { background: #ff8a80 !important; }
      .lois-c-peach  { background: #ffcc80 !important; }
      .lois-c-mint   { background: #80cbc4 !important; }
      .lois-c-lilac  { background: #ce93d8 !important; }
      .lois-c-sky    { background: #81d4fa !important; }
      .lois-c-yellow { background: #fff176 !important; }
      .lois-c-green  { background: #a5d6a7 !important; }
      @keyframes loisConfettiFall {
        0%   { opacity: 0; transform: translateY(-18px) rotate(0deg) scale(0.7); }
        8%   { opacity: 0.75; }
        85%  { opacity: 0.45; }
        100% { opacity: 0; transform: translateY(58px) rotate(420deg) scale(0.3); }
      }
      @media (max-width: 768px) {
        #lois-custom-banner {
          white-space: normal;
          line-height: 1.4 !important;
          padding: 10px 16px !important;
          font-size: 13px !important;
        }
        #lois-custom-banner .banner-part1 {
          display: block;
        }
        #lois-custom-banner .banner-part2 {
          display: block;
          margin-top: 2px;
        }
      }
      @media (max-width: 480px) {
        #lois-custom-banner {
          padding: 8px 12px !important;
          font-size: 12px !important;
          line-height: 1.35 !important;
        }
        #lois-custom-banner .banner-part2 {
          margin-top: 1px;
        }
      }
    `;
    (document.head || document.documentElement).appendChild(styleEl);

    // Set banner content (no <style> tag inside — styles are in <head>)
    banner.innerHTML = `
      ${confettiHTML}
      ${part1HTML}<span class="banner-part2"><strong>${bannerText.part2}</strong></span>
    `;
    
    // Insert banner at the very beginning of body or documentElement.
    // Banner is in normal document flow (position: relative), so it occupies its own
    // height — no body padding adjustment needed.
    function insertBanner() {
      if (document.body) {
        if (document.body.firstChild) {
          document.body.insertBefore(banner, document.body.firstChild);
        } else {
          document.body.appendChild(banner);
        }
      } else if (document.documentElement) {
        document.documentElement.insertBefore(banner, document.documentElement.firstChild);
      }
    }
    
    if (document.body || document.documentElement) {
      insertBanner();
    } else {
      // If body not ready, wait for it
      document.addEventListener('DOMContentLoaded', insertBanner);
      // Also try on load as backup
      window.addEventListener('load', insertBanner);
    }
  }

  // Translate announcement bar message (only when on Italian site)
  // Automatically detects which German text is present and replaces it with Italian
  function translateAnnouncementBar(lang) {
    // Only translate if we're on the Italian site
    if (lang !== 'it') {
      return;
    }

    // Try multiple selectors: Essential Announcement Bar first, then AppHero
    let announcementElement = document.querySelector(config.selectors.announcementTitle);
    
    if (!announcementElement) {
      announcementElement = document.querySelector(config.selectors.announcementTitleClass);
    }
    
    // Try AppHero banner selector
    if (!announcementElement) {
      announcementElement = document.querySelector(config.selectors.appHeroBanner);
    }
    
    if (!announcementElement) {
      return;
    }

    const currentText = announcementElement.textContent.trim();

    // Find matching German text and get corresponding Italian translation
    // Check if current text contains the German text (to handle emojis or extra characters)
    // Note: This function is kept for backwards compatibility but may not be needed anymore
    const isGerman = currentText.includes(bannerTexts.de) || currentText === bannerTexts.de;
    const isItalian = currentText.includes(bannerTexts.it) || currentText === bannerTexts.it;

    if (isGerman && lang === 'it') {
      const iconElement = announcementElement.querySelector('i');
      const iconClone = iconElement ? iconElement.cloneNode(true) : null;

      announcementElement.textContent = bannerTexts.it;

      if (iconClone) {
        announcementElement.insertBefore(iconClone, announcementElement.firstChild);
        announcementElement.insertBefore(document.createTextNode(' '), iconClone.nextSibling);
      }
    }
  }

  // Translate "Shop now!" button (only when on Italian site)
  function translateShopNowButton(lang) {
    // Only translate if we're on the Italian site
    if (lang !== 'it') {
      return;
    }

    // Find the Shop now button using the class selector
    const button = document.querySelector(config.selectors.shopNowButton);
    
    if (!button) {
      return;
    }

    const currentText = button.textContent.trim();
    
    // Check if this is the Shop now button (could be in English or German)
    if (currentText === "Shop now!" || currentText === translations.de.shopNow) {
      button.textContent = translations.it.shopNow;
    }
  }

  // Main update function
  function updateElements() {
    const lang = getPageLanguage();
    
    try {
      translateAnnouncementBar(lang);
      translateShopNowButton(lang);
    } catch (error) {
      // Silently handle errors
    }
  }

  // Set up mutation observer to handle dynamically loaded content
  function observeDOMChanges() {
    const observer = new MutationObserver(function(mutations) {
      // Only update if announcement bar related changes occurred
      const shouldUpdate = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) { // Element node
            return node.matches && (
              node.matches('[class*="essential_annoucement_bar"]') ||
              node.matches('[class*="aph_bar"]') ||
              node.closest('[class*="essential_annoucement_bar"]') ||
              node.closest('[class*="aph_bar"]')
            );
          }
          return false;
        });
      });

      if (shouldUpdate) {
        updateElements();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  function init() {
    // Create custom banner first (before other content)
    createCustomBanner();
    
    // Apply translations immediately
    updateElements();
    
    // Set up observer for dynamic content
    observeDOMChanges();
  }

  // Start immediately to create banner as early as possible
  // Try to create banner even before DOMContentLoaded for faster display
  if (document.body) {
    createCustomBanner();
  }
  
  // Full initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

})();

